import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { fetchProfile, updateProfile } from "../../redux/account";
import Navbar from "../../components/layout/Navbar";
import Footer from "../../components/layout/Footer";
import ChangeProfilePictureModal from "../../components/modals/ChangeProfilePictureModal";
import "../../components/styles/accountPage.scss";

/**
 * AccountPage — Account profile settings
 * Figma: Account (Desktop) (24654:5420) — 1920×1050
 *
 * Fetches real profile from CRM subscriber API, allows editing name/email/dob.
 */

const AccountPage = () => {
  const navigate = useNavigate();
  const { profile, isProfileLoaded } = useSelector((state) => state.account);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState(null);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    dob: "",
  });

  // Fetch profile on mount if not already loaded
  useEffect(() => {
    if (!isProfileLoaded) {
      fetchProfile();
    }
  }, [isProfileLoaded]);

  // Sync form data when profile loads from Redux
  useEffect(() => {
    if (isProfileLoaded) {
      setFormData({
        firstName: profile.first_name || "",
        lastName: profile.last_name || "",
        email: profile.email || "",
        dob: profile.date_of_birth || "",
      });
    }
  }, [isProfileLoaded, profile]);

  const handleChange = (field) => (e) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
    setSaveMessage(null);
  };

  const handleSave = useCallback(async () => {
    if (isSaving) return;
    setIsSaving(true);
    setSaveMessage(null);

    const success = await updateProfile(
      formData.firstName.trim(),
      formData.lastName.trim(),
      formData.dob.trim() || undefined
    );

    setIsSaving(false);
    if (success) {
      // Re-fetch profile to confirm the update persisted
      await fetchProfile();
      setSaveMessage({ type: "success", text: "Profile updated successfully" });
    } else {
      setSaveMessage({ type: "error", text: "Failed to update profile. Please try again." });
    }
  }, [isSaving, formData.firstName, formData.lastName, formData.dob]);

  // Display name + initial for avatar
  const displayName =
    `${profile.first_name} ${profile.last_name}`.trim() || "User";
  const initial = displayName.charAt(0).toUpperCase();

  return (
    <div className="account-page">
      <Navbar activeTab="" />

      <div className="account-page__body">
        {/* Breadcrumb */}
        <div className="account-page__breadcrumb">
          <span
            className="account-page__breadcrumb-link"
            onClick={() => navigate("/settings")}
            style={{ cursor: "pointer" }}
          >
            Setting
          </span>
          <span className="account-page__breadcrumb-sep">›</span>
          <span className="account-page__breadcrumb-current">Account</span>
        </div>

        {/* Account card */}
        <div className="account-page__card">
          <h2 className="account-page__card-title">Account</h2>

          {/* Profile header */}
          <div className="account-page__profile-header">
            <div className="account-page__avatar">
              <svg width="56" height="56" viewBox="0 0 56 56" fill="none">
                <circle cx="28" cy="28" r="27" fill="#1a1a6e" stroke="#2a2a8e" strokeWidth="1" />
                <text x="28" y="33" textAnchor="middle" fill="white" fontSize="22" fontWeight="600">
                  {initial}
                </text>
              </svg>
            </div>
            <div className="account-page__profile-info">
              <span className="account-page__profile-name">{displayName}</span>
              <span className="account-page__profile-email">
                {profile.email || "No email set"}
              </span>
            </div>
            <button
              className="account-page__edit-pic-btn"
              onClick={() => setShowProfileModal(true)}
            >
              Edit profile picture
            </button>
          </div>

          {/* Form fields */}
          <div className="account-page__form">
            <div className="account-page__field">
              <label className="account-page__label">First name</label>
              <input
                type="text"
                className="account-page__input"
                value={formData.firstName}
                onChange={handleChange("firstName")}
                placeholder="Enter first name"
              />
            </div>
            <div className="account-page__field">
              <label className="account-page__label">Last name</label>
              <input
                type="text"
                className="account-page__input"
                value={formData.lastName}
                onChange={handleChange("lastName")}
                placeholder="Enter last name"
              />
            </div>
            <div className="account-page__field">
              <label className="account-page__label">Email</label>
              <input
                type="email"
                className="account-page__input account-page__input--disabled"
                value={formData.email}
                readOnly
              />
            </div>
            <div className="account-page__field">
              <label className="account-page__label">Date of birth</label>
              <input
                type="text"
                className="account-page__input"
                value={formData.dob}
                onChange={handleChange("dob")}
                placeholder="DD/MM/YYYY"
              />
            </div>

            {/* Save message */}
            {saveMessage && (
              <p
                className={`account-page__message account-page__message--${saveMessage.type}`}
              >
                {saveMessage.text}
              </p>
            )}

            <button
              className="account-page__save-btn"
              onClick={handleSave}
              disabled={isSaving}
            >
              {isSaving ? "Saving..." : "Save changes"}
            </button>
          </div>
        </div>
      </div>

      <Footer />

      {/* Change Profile Picture Modal */}
      <ChangeProfilePictureModal
        isOpen={showProfileModal}
        onClose={() => setShowProfileModal(false)}
      />
    </div>
  );
};

export default AccountPage;
